import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { InsightService } from '../insight-new/insight.service';
import { generateDataFilterOptions } from '../performance-data/data-filter-options.pd.model';
import { MetricData } from '../performance-data/metric.pd.model';
import { PerformanceDataService } from '../performance-data/performance-data.service';
import { Dependencies } from '../store';
import { pluck } from '../store.decorator';
import { PayloadAction } from '../store.model';

import { CardType } from './models/card-type.model';
import { CriteriaIdType } from './models/criteria-type.model';
import { PerformanceCard } from './models/performance-card.model';
import { PerformancePeriodCompare } from './models/period-compare.model';
import { PerformancePopulationCompare } from './models/population-compare.model';
import {
    ActionType,
    reloadResult,
    ReloadResultAction,
    RootReloadAction,
    SelectPeriodAction,
    selectPeriodResult,
    SelectPeriodResultAction,
    SelectPopulationAction,
    selectPopulationResult,
    SelectPopulationResultAction,
} from './my-performance-card.action';
import { PerformanceCardState } from './my-performance-card.state';

export interface ChainGroup<TPayload> {
    root: TPayload;

    performanceCard?: PerformanceCard;
    periodCompare?: PerformancePeriodCompare;
    populationCompare?: PerformancePopulationCompare;
}

const reload = (
    action: ActionsObservable<PayloadAction<ActionType, RootReloadAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
): Observable<PayloadAction<ActionType, ReloadResultAction>> =>
    action.pipe(
        ofType(ActionType.MY_PERFORMANCE_CARD__ROOT__RELOAD),
        switchMap((act) => {
            const service = deps.injector.get<InsightService>(InsightService);
            const grp: ChainGroup<RootReloadAction> = { root: act.payload };
            const obses: [
                Observable<PerformanceCard>,
                Observable<PerformancePeriodCompare>,
            ] = [
                of(null).pipe(
                    service.retrievePerformanceCard(() => ({
                        userId: act.payload.user.id,
                        cardType: CardType.DEFAULT,
                    })),
                    tap((ret) => {
                        grp.performanceCard = ret;
                    }),
                ),
                of(null).pipe(
                    service.retrievePerformancePeriodCompare(() => ({
                        userId: act.payload.user.id,
                    })),
                    tap((ret) => {
                        grp.periodCompare = ret;
                    }),
                ),
            ];
            return forkJoin(obses).pipe(
                service.retrievePerformancePopulationCompare(() => ({
                    userId: act.payload.user.id,
                    periodTypeId: grp.performanceCard.periodTypeId,
                })),
                tap((ret) => {
                    grp.populationCompare = ret;
                }),
                map(() => {
                    return reloadResult(
                        {
                            ...act.payload,
                            performanceCard: grp.performanceCard,
                            periodCompare: grp.periodCompare,
                            populationCompare: grp.populationCompare,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadResult({ ...act.payload }, act.storePath)),
                ),
            );
        }),
    );

const selectPeriod = (
    action: ActionsObservable<PayloadAction<ActionType, SelectPeriodAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
): Observable<PayloadAction<ActionType, SelectPeriodResultAction>> =>
    action.pipe(
        ofType(ActionType.MY_PERFORMANCE_CARD_SELECT_PERIOD_COMPARE),
        switchMap((act) => {
            const service = deps.injector.get<InsightService>(InsightService);
            const rootState = pluck<PerformanceCardState>(
                state.value,
                act.storePath,
            );

            return of(null).pipe(
                service.retrievePerformanceCard(() => ({
                    userId: act.payload.user.id,
                    periodTypeId: act.payload.selectedDate?.periodType,
                    eventDate: new Date(act.payload.selectedDate?.endDate),
                    definitionIdentifier:
                        rootState.performanceCard.definitionIdentifier,
                    cardType: act.payload.cardType,
                })),
                map((ret) => {
                    return selectPeriodResult(
                        {
                            ...act.payload,
                            performanceCard: ret,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(selectPeriodResult({ ...act.payload }, act.storePath)),
                ),
            );
        }),
    );

const selectPopulation = (
    action: ActionsObservable<
        PayloadAction<ActionType, SelectPopulationAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
): Observable<PayloadAction<ActionType, SelectPopulationResultAction>> =>
    action.pipe(
        ofType(ActionType.MY_PERFORMANCE_CARD_SELECT_POPULATION_COMPARE),
        switchMap((act) => {
            const _performanceDataService =
                deps.injector.get<PerformanceDataService>(
                    PerformanceDataService,
                );
            const rootState = pluck<PerformanceCardState>(
                state.value,
                act.storePath,
            );

            const opts = generateDataFilterOptions({
                viewType: Number(Object.keys(act.payload.selectedData)[0]),
                periodType: rootState.performanceCard.periodTypeId,
                date: new Date(rootState.performanceCard.endDate),
                lobIdList: act.payload.selectedData[CriteriaIdType.LOB_ID],
                regionIdList:
                    act.payload.selectedData[CriteriaIdType.REGION_ID],
                areaIdList: act.payload.selectedData[CriteriaIdType.AREA_ID],
                siteIdList: act.payload.selectedData[CriteriaIdType.SITE_ID],
                peerGroupIdList:
                    act.payload.selectedData[CriteriaIdType.PEER_GROUP_ID],
                superGroupIdList:
                    act.payload.selectedData[CriteriaIdType.SUPER_GROUP_ID],
                queueIdList: act.payload.selectedData[CriteriaIdType.QUEUE_ID],
                metricIdList: act.payload.metricsIds,
                displayNameCriteriaIdType: null,
                outputCriteriaIdType: null,
            });

            return _performanceDataService.listData(opts).pipe(
                map((ret: MetricData[]) => {
                    return selectPopulationResult(
                        {
                            ...act.payload,
                            metrics: ret,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(selectPeriodResult({ ...act.payload }, act.storePath)),
                ),
            );
        }),
    );

export const performanceCardEpics = [reload, selectPeriod, selectPopulation];
